import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Product, ProductVariant, getDisplayCategory } from '../types/product';
import { groupProductsIntoVariants, sortVariants } from '../utils/variantUtils';
import { ProductCollection, COLLECTIONS } from '../types/collection';
import { galwayCrystalService } from '../services/galwayCrystalService';

interface ProductContextType {
  products: Product[];
  productVariants: ProductVariant[];
  // Combined collections
  allProducts: Product[];
  allProductVariants: ProductVariant[];
  trophiesProducts: Product[];
  trophiesVariants: ProductVariant[];
  galwayProducts: Product[];
  loading: boolean;
  error: string | null;
  currentCollection: ProductCollection;
  setCollection: (collection: ProductCollection) => void;
  getProductById: (id: string) => Product | undefined;
  getProductVariants: (productName: string) => Product[];
  getVariantByBaseId: (baseId: string) => ProductVariant | undefined;
  getRibbons: () => Product[];
  searchTerm: string;
  setSearchTerm: (term: string) => void;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export const useProducts = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProducts must be used within ProductProvider');
  }
  return context;
};

interface ProductProviderProps {
  children: ReactNode;
}

const transformImagePath = (imagePath: string): string => {
  // Transform "Product Images/ocr_crops/filename.png" to "/assets/Product Images/ocr_crops/filename.png"
  if (imagePath.startsWith('Product Images/')) {
    return `/assets/${imagePath}`;
  }
  // Transform "images/products/filename.png" to "/assets/products/filename.png"
  if (imagePath.startsWith('images/products/')) {
    return imagePath.replace('images/products/', '/assets/products/');
  }
  // If already in correct format, return as is
  if (imagePath.startsWith('/assets/')) {
    return imagePath;
  }
  // Default fallback
  return `/assets/products/${imagePath}`;
};

const loadProducts = async (): Promise<Product[]> => {
  try {
    const response = await fetch('/assets/product_data/products.json');
    if (!response.ok) {
      throw new Error('Failed to load products');
    }
    const data = await response.json();

    // Transform the data
    return data.map((item: any) => ({
      ...item,
      images: item.imagePath ? [transformImagePath(item.imagePath)] : [],
      price: item.price || 0,
      sku: item.productId || item.id,
      stockQuantity: 100, // Default stock
    }));
  } catch (error) {
    console.error('Error loading products:', error);
    throw error;
  }
};

export const ProductProvider: React.FC<ProductProviderProps> = ({ children }) => {
  const [trophiesProducts, setTrophiesProducts] = useState<Product[]>([]);
  const [trophiesVariants, setTrophiesVariants] = useState<ProductVariant[]>([]);
  const [galwayProducts, setGalwayProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentCollection, setCurrentCollection] = useState<ProductCollection>(
    COLLECTIONS.TROPHIES_MEDALS
  );
  const [searchTerm, setSearchTerm] = useState('');

  // Load all products from both collections
  useEffect(() => {
    const loadAllProducts = async () => {
      try {
        setLoading(true);

        // Load Trophies & Medals
        const loadedTrophiesProducts = await loadProducts();
        setTrophiesProducts(loadedTrophiesProducts);
        const variants = groupProductsIntoVariants(loadedTrophiesProducts);
        const sortedVariants = sortVariants(variants);
        setTrophiesVariants(sortedVariants);

        // Load Galway Crystal
        await galwayCrystalService.loadGalwayCrystalProducts();
        const galwayProducts = galwayCrystalService.getGalwayProducts();
        setGalwayProducts(galwayProducts);

        setError(null);
      } catch (err) {
        setError('Failed to load products');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadAllProducts();
  }, []);

  // Computed values for backward compatibility
  const products = currentCollection === COLLECTIONS.GALWAY_CRYSTAL ? galwayProducts : trophiesProducts;
  const productVariants = currentCollection === COLLECTIONS.TROPHIES_MEDALS ? trophiesVariants : [];

  const setCollection = (collection: ProductCollection) => {
    console.log(`🔄 Switching collection to: ${collection}`);
    setCurrentCollection(collection);
  };

  const getProductById = (id: string): Product | undefined => {
    // Search in all products from both collections
    const allProducts = [...trophiesProducts, ...galwayProducts];
    return allProducts.find((p) => p.id === id || p.productId === id || p.sku === id);
  };

  const getProductVariants = (productName: string): Product[] => {
    // Get all products with the same base name
    return products.filter((p) => p.name === productName);
  };

  const getVariantByBaseId = (baseId: string): ProductVariant | undefined => {
    return productVariants.find((v) => v.baseId === baseId);
  };

  const getRibbons = (): Product[] => {
    // Get all ribbon products from trophies collection
    const allRibbons = trophiesProducts.filter(
      (p) => getDisplayCategory(p).toLowerCase() === 'ribbon'
    );

    // Exclude rosettes (product IDs starting with "RS-")
    const ribbons = allRibbons.filter(
      (p) => !p.productId.startsWith('RS-')
    );

    return ribbons;
  };

  // Combined products from both collections
  const allProducts = [...trophiesProducts, ...galwayProducts];
  const allProductVariants = trophiesVariants;

  const value = {
    products,
    productVariants,
    allProducts,
    allProductVariants,
    trophiesProducts,
    trophiesVariants,
    galwayProducts,
    loading,
    error,
    currentCollection,
    setCollection,
    getProductById,
    getProductVariants,
    getVariantByBaseId,
    getRibbons,
    searchTerm,
    setSearchTerm,
  };

  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>;
};
